import { useState } from 'react';
import { DeleteOutlined, DownloadOutlined, ExportOutlined, ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Divider,
  Drawer,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import type { MaterialAdmin } from '@titularis/api-client';
import { api } from '../api';
import { formatarData } from '../dominio';
import { baixarBlob } from '../arquivo';
import {
  caminhoValido,
  chavesDeOutroMaterial,
  chavesRepetidas,
  chavesSemDestino,
  entradaDoMaterial,
  FORMATO_DE_ENDERECO,
  limiteDoDestino,
  linhasDoJson,
  situacaoDoMaterial,
  TAMANHO_DA_CHAVE,
  TAMANHO_DO_DESTINO,
  valoresDoMaterial,
  type ValoresDoMaterial,
} from '../pages/materiais/material';
import { enderecoDoLink, enderecoDoMaterial } from '../site';
import { EnvioDeArquivo } from './EnvioDeArquivo';
import { GraficoDeUso, Numero } from './UsoDoArquivo';

/**
 * O material ocupa uma gaveta, como o artigo da Revista: links e cópias fazem a
 * tela crescer.
 *
 * **O que esta gaveta tem que a da Revista não tem é o PDF distribuído.** O
 * endereço e as chaves estão impressos em arquivos que circulam em grupos, e a
 * tela existe para ligá-los a destinos (G-PERMALINK), para dizer em voz alta o
 * que o PDF aponta e não está ligado (G-AMBIGUITY), e para gerar a cópia de
 * cada grupo que a casa semeia.
 */
export function FormularioDoMaterial({
  materialId,
  onFechar,
}: {
  materialId: string | null;
  onFechar: () => void;
}) {
  const material = useQuery({
    queryKey: ['materiais', 'material', materialId],
    queryFn: () => api.getMaterialById(materialId!),
    enabled: Boolean(materialId),
  });

  return (
    <Drawer
      open
      title={materialId ? 'Editar material' : 'Novo material'}
      size="min(900px, 100vw)"
      onClose={onFechar}
      destroyOnHidden
    >
      {materialId && material.isPending ? (
        <Skeleton active />
      ) : materialId && material.error ? (
        <Alert
          type="error"
          showIcon
          title={material.error.message}
          action={<Button onClick={() => void material.refetch()}>Tentar novamente</Button>}
        />
      ) : (
        <CamposDoMaterial material={material.data ?? null} onFechar={onFechar} />
      )}
    </Drawer>
  );
}

function CamposDoMaterial({
  material,
  onFechar,
}: {
  material: MaterialAdmin | null;
  onFechar: () => void;
}) {
  const { token } = theme.useToken();
  const { message: mensagem } = App.useApp();
  const consultas = useQueryClient();
  const [formulario] = Form.useForm<ValoresDoMaterial>();
  const [salvo, setSalvo] = useState(material);
  const [importando, setImportando] = useState(false);

  const receberSalvo = async (dados: MaterialAdmin) => {
    setSalvo(dados);
    consultas.setQueryData(['materiais', 'material', dados.id], dados);
    // Só a lista: o cadastro acabou de chegar na resposta, e o engajamento não muda com ele.
    await consultas.invalidateQueries({ queryKey: ['materiais', 'lista'] });
  };
  const salvar = useMutation({
    mutationFn: (valores: ValoresDoMaterial) =>
      salvo
        ? api.updateMaterial(salvo.id, entradaDoMaterial(valores))
        : api.createMaterial(entradaDoMaterial(valores)),
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      formulario.setFieldValue('slug', dados.slug);
      mensagem.success('Material salvo.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const enviarCapa = useMutation({
    mutationFn: (arquivo: File) => api.uploadMaterialCover(salvo!.id, arquivo),
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      mensagem.success('Capa enviada.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  const enviarPdf = useMutation({
    mutationFn: (arquivo: File) => api.uploadMaterialPdf(salvo!.id, arquivo),
    onSuccess: async (dados) => {
      await receberSalvo(dados);
      mensagem.success('PDF enviado.');
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });
  // As três escritas devolvem o cadastro inteiro: serializar evita uma resposta
  // antiga esconder o que acabou de ser salvo, como na Revista.
  const ocupado = salvar.isPending || enviarCapa.isPending || enviarPdf.isPending;
  const noAr = salvo ? situacaoDoMaterial(salvo.publishedAt) === 'no_ar' : false;
  const endereco = salvo ? enderecoDoMaterial(salvo.slug) : null;

  return (
    <>
      <Form
        form={formulario}
        layout="vertical"
        onFinish={(valores) => salvar.mutate(valores)}
        initialValues={{
          ...valoresDoMaterial(material),
          publishedAt: material?.publishedAt ? dayjs(material.publishedAt) : null,
        }}
      >
        <Form.Item
          name="title"
          label="Título"
          rules={[{ required: true, whitespace: true, message: 'O material precisa de um título.' }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          name="slug"
          label="Endereço"
          extra={
            salvo?.temPdf
              ? 'Travado: o endereço já está dentro do PDF, e trocá-lo mataria os links das cópias que circulam.'
              : 'Vazio, sai do título. Ele vai para dentro dos links do PDF: escolha antes de gerar o arquivo.'
          }
          rules={[
            {
              pattern: FORMATO_DE_ENDERECO,
              message: 'Só letras minúsculas sem acento, números e hífen.',
            },
          ]}
        >
          <Input
            maxLength={80}
            disabled={Boolean(salvo?.temPdf)}
            prefix={<Typography.Text type="secondary">/materiais/</Typography.Text>}
            placeholder="cronograma-enac-4"
          />
        </Form.Item>
        <Form.Item
          name="summary"
          label="Chamada"
          extra="Aparece no cartão da lista, na busca e na prévia do link no WhatsApp."
          rules={[{ required: true, whitespace: true, message: 'O material precisa de uma chamada.' }]}
        >
          <Input.TextArea rows={2} maxLength={200} showCount />
        </Form.Item>
        <Form.Item name="description" label="Descrição" extra="Parágrafos separados por uma linha em branco.">
          <Input.TextArea rows={5} maxLength={20000} />
        </Form.Item>
        <Form.Item
          name="fileName"
          label="Nome do arquivo"
          extra="É o nome que aparece no balão do WhatsApp. Vazio, sai do título."
        >
          <Input maxLength={150} placeholder="Cronograma de Estudos Titularis - ENAC 4.pdf" />
        </Form.Item>
        <Form.Item
          name="publishedAt"
          label="Publicação"
          extra="Vazio deixa em rascunho. Data futura agenda. Data passada põe no ar."
        >
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
        </Form.Item>

        <Divider titlePlacement="start">Links do PDF</Divider>
        <Typography.Paragraph type="secondary">
          Todo link do PDF aponta para{' '}
          <Typography.Text code>
            {enderecoDoLink(salvo?.slug ?? 'endereco', 'chave')}
          </Typography.Text>{' '}
          e é resolvido aqui: trocar o destino muda para onde vão as cópias que já circulam.
          Chave sem destino vai ao padrão; sem padrão, à página do material.
        </Typography.Paragraph>
        <Form.Item label="Destino padrão">
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item name="padraoOnde" noStyle>
              <Select
                style={{ width: 110 }}
                options={[
                  { value: 'app', label: 'App' },
                  { value: 'site', label: 'Site' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="padraoCaminho"
              noStyle
              rules={[
                {
                  validator: async (_, valor?: string) => {
                    if (valor?.trim() && !caminhoValido(valor.trim())) {
                      throw new Error('Um caminho interno, começando com uma barra.');
                    }
                    if (valor && valor.trim().length > TAMANHO_DO_DESTINO) {
                      throw new Error(`O destino ${limiteDoDestino(valor.trim())}.`);
                    }
                  },
                },
              ]}
            >
              <Input placeholder="/estudar?module=enac" />
            </Form.Item>
          </Space.Compact>
        </Form.Item>
        <Form.List
          name="chaves"
          rules={[
            {
              validator: async (_, linhas: ValoresDoMaterial['chaves']) => {
                const repetidas = chavesRepetidas(linhas);
                if (repetidas.length) {
                  throw new Error(`Chave repetida: ${repetidas.join(', ')}. Cada chave leva a um destino só.`);
                }
              },
            },
          ]}
        >
          {(campos, { add: acrescentar, remove: remover }, { errors: erros }) => (
            <Space orientation="vertical" size={token.marginXS} style={{ width: '100%', marginBottom: token.marginLG }}>
              {campos.map(({ key: chave, name: indice }) => (
                <Flex key={chave} gap={token.marginXS} align="flex-start">
                  <Form.Item
                    name={[indice, 'chave']}
                    style={{ margin: 0, width: 180 }}
                    rules={[
                      { required: true, message: 'A chave.' },
                      { pattern: FORMATO_DE_ENDERECO, message: 'Minúsculas, números e hífen.' },
                    ]}
                  >
                    <Input placeholder="2026-09-28" maxLength={TAMANHO_DA_CHAVE} />
                  </Form.Item>
                  <Form.Item name={[indice, 'onde']} style={{ margin: 0 }} initialValue="app">
                    <Select
                      style={{ width: 90 }}
                      options={[
                        { value: 'app', label: 'App' },
                        { value: 'site', label: 'Site' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[indice, 'caminho']}
                    style={{ margin: 0, flex: 1 }}
                    rules={[
                      {
                        validator: async (_, valor?: string) => {
                          if (!valor?.trim() || !caminhoValido(valor.trim())) {
                            throw new Error('Um caminho interno, começando com uma barra.');
                          }
                          if (valor.trim().length > TAMANHO_DO_DESTINO) {
                            throw new Error(`O destino ${limiteDoDestino(valor.trim())}.`);
                          }
                        },
                      },
                    ]}
                  >
                    <Input placeholder="/estudar?module=enac&questoes=..." />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Remover o link ${indice + 1}`}
                    onClick={() => remover(indice)}
                  />
                </Flex>
              ))}
              <Form.ErrorList errors={erros} />
              <Flex gap={token.marginXS} wrap>
                <Button icon={<PlusOutlined />} onClick={() => acrescentar({ onde: 'app' })}>
                  Acrescentar link
                </Button>
                <Button icon={<ImportOutlined />} onClick={() => setImportando(true)}>
                  Importar JSON
                </Button>
              </Flex>
            </Space>
          )}
        </Form.List>

        <Divider titlePlacement="start">Arquivos</Divider>
        <EnvioDeArquivo
          tipo="capa"
          salvo={Boolean(salvo)}
          temArquivo={salvo?.temCapa ?? false}
          enviando={enviarCapa.isPending}
          desabilitado={ocupado}
          onEnviar={(arquivo) => enviarCapa.mutate(arquivo)}
          dica="JPEG, PNG ou WebP, até 5 MB. Em pé, como a primeira página do PDF: é ela que vira o cartão da prévia."
        />
        <EnvioDeArquivo
          tipo="pdf"
          salvo={Boolean(salvo)}
          temArquivo={salvo?.temPdf ?? false}
          enviando={enviarPdf.isPending}
          desabilitado={ocupado}
          onEnviar={(arquivo) => enviarPdf.mutate(arquivo)}
          dica="PDF, até 30 MB, sem senha. Ele é conferido para a marca de cópia antes de ser aceito, e o envio substitui o anterior."
        />
        {salvo?.pdfInventory && <Inventario material={salvo} />}

        <Flex gap={token.marginSM} justify="flex-end" style={{ marginTop: token.marginLG }}>
          <Button onClick={onFechar} disabled={ocupado}>
            Fechar
          </Button>
          <Button type="primary" htmlType="submit" loading={salvar.isPending} disabled={ocupado}>
            Salvar
          </Button>
        </Flex>
      </Form>

      {importando && (
        <ImportarLinks
          onFechar={() => setImportando(false)}
          onImportar={({ linhas, padrao }) => {
            const atuais = (formulario.getFieldValue('chaves') ?? []) as ValoresDoMaterial['chaves'];
            const preenchidas = (atuais ?? []).filter((linha) => linha?.chave?.trim());
            const porChave = new Map(preenchidas.map((linha) => [linha.chave.trim(), linha]));
            for (const linha of linhas) porChave.set(linha.chave, linha);
            formulario.setFieldValue('chaves', [...porChave.values()]);
            if (padrao) {
              formulario.setFieldValue('padraoOnde', padrao.onde);
              formulario.setFieldValue('padraoCaminho', padrao.caminho);
            }
            setImportando(false);
            mensagem.info(`${linhas.length} links importados. Salve para gravar.`);
          }}
        />
      )}

      {salvo && endereco && (
        <>
          <Divider />
          <Typography.Text type="secondary">
            {noAr ? 'Endereço público do material:' : 'Endereço público, disponível quando o material estiver no ar:'}
          </Typography.Text>
          <Typography.Paragraph copyable={{ text: endereco }} style={{ overflowWrap: 'anywhere' }}>
            {noAr ? (
              <a href={endereco} target="_blank" rel="noopener noreferrer">
                {endereco} <ExportOutlined />
              </a>
            ) : (
              endereco
            )}
          </Typography.Paragraph>
          {salvo.temPdf && <CopiaDeGrupo material={salvo} />}
          <UsoDoMaterial materialId={salvo.id} />
        </>
      )}
    </>
  );
}

/**
 * O que o último PDF enviado aponta, em voz alta (G-AMBIGUITY).
 *
 * Link direto para a Titularis e chave sem destino não quebram nada, e é
 * justamente por isso que precisam aparecer: o clique chega, mas sem dizer de
 * qual cópia veio, ou vai para um lugar que ninguém escolheu.
 */
function Inventario({ material }: { material: MaterialAdmin }) {
  const { token } = theme.useToken();
  const inventario = material.pdfInventory!;
  const semDestino = chavesSemDestino(material);
  const deOutro = chavesDeOutroMaterial(material);

  return (
    <Space orientation="vertical" size={token.marginXS} style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        O PDF aponta {inventario.chaves.length} {inventario.chaves.length === 1 ? 'chave' : 'chaves'} de material.
      </Typography.Text>
      {inventario.diretos.length > 0 && (
        <Alert
          type="warning"
          showIcon
          title={`${inventario.diretos.length} ${inventario.diretos.length === 1 ? 'link aponta' : 'links apontam'} direto para a Titularis`}
          description={
            <>
              {inventario.diretos.length === 1 ? 'Ele funciona' : 'Eles funcionam'}, mas o clique chega sem
              dizer de qual cópia nem de qual trecho veio. Troque no arquivo por um endereço de material:{' '}
              {inventario.diretos.slice(0, 5).join(', ')}
              {inventario.diretos.length > 5 ? '…' : ''}
            </>
          }
        />
      )}
      {semDestino.length > 0 && (
        <Alert
          type={material.links.padrao ? 'info' : 'warning'}
          showIcon
          title={`${semDestino.length} ${semDestino.length === 1 ? 'chave do PDF não tem' : 'chaves do PDF não têm'} destino próprio`}
          description={`${
            material.links.padrao
              ? semDestino.length === 1
                ? 'Ela vai ao destino padrão'
                : 'Elas vão ao destino padrão'
              : semDestino.length === 1
                ? 'Sem destino padrão, ela leva à página do material'
                : 'Sem destino padrão, elas levam à página do material'
          }: ${semDestino.slice(0, 8).join(', ')}${semDestino.length > 8 ? '…' : ''}`}
        />
      )}
      {deOutro.length > 0 && (
        <Alert
          type="warning"
          showIcon
          title="O PDF tem links de outro material"
          description={`Eles funcionam, mas contam para o outro: ${deOutro.slice(0, 5).join(', ')}`}
        />
      )}
      {inventario.externos.length > 0 && (
        <Typography.Text type="secondary">
          Links externos: {inventario.externos.slice(0, 3).join(', ')}
          {inventario.externos.length > 3 ? ` e mais ${inventario.externos.length - 3}` : ''}.
        </Typography.Text>
      )}
    </Space>
  );
}

function ImportarLinks({
  onFechar,
  onImportar,
}: {
  onFechar: () => void;
  onImportar: (resultado: ReturnType<typeof linhasDoJson>) => void;
}) {
  const { token } = theme.useToken();
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  return (
    <Modal
      open
      title="Importar links em JSON"
      okText="Importar"
      cancelText="Cancelar"
      onCancel={onFechar}
      onOk={() => {
        try {
          onImportar(linhasDoJson(texto));
        } catch (falha) {
          setErro((falha as Error).message);
        }
      }}
    >
      <Typography.Paragraph type="secondary">
        Um objeto de chaves, como{' '}
        <Typography.Text code>{'{"2026-09-28": {"onde": "app", "caminho": "/estudar?..."}}'}</Typography.Text>, ou
        o objeto inteiro de links, com <Typography.Text code>padrao</Typography.Text> e{' '}
        <Typography.Text code>chaves</Typography.Text>. Chave repetida substitui a que já está na tabela.
      </Typography.Paragraph>
      <Input.TextArea
        rows={10}
        value={texto}
        onChange={(evento) => {
          setTexto(evento.target.value);
          setErro(null);
        }}
        style={{ fontFamily: 'monospace' }}
      />
      {erro && <Alert type="error" showIcon title={erro} style={{ marginTop: token.marginSM }} />}
    </Modal>
  );
}

/**
 * A cópia de um grupo: o PDF marcado, com um rótulo que o engajamento mostra.
 *
 * É o que a casa manda em cada grupo que semeia. O código dentro dela faz os
 * cliques e as contas que vierem desse grupo aparecerem com o nome dele, mesmo
 * depois de o arquivo ser encaminhado para outros.
 */
function CopiaDeGrupo({ material }: { material: MaterialAdmin }) {
  const { token } = theme.useToken();
  const { message: mensagem } = App.useApp();
  const consultas = useQueryClient();
  const [rotulo, setRotulo] = useState('');

  const gerar = useMutation({
    mutationFn: (nome: string) => api.materialGroupCopy(material.id, nome),
    onSuccess: async ({ blob, nome }) => {
      baixarBlob(blob, nome);
      setRotulo('');
      mensagem.success('Cópia gerada. Mande este arquivo no grupo.');
      await consultas.invalidateQueries({ queryKey: ['materiais', 'engajamento', material.id] });
    },
    onError: (erro: Error) => mensagem.error(erro.message),
  });

  return (
    <Space orientation="vertical" size={token.marginXS} style={{ width: '100%', marginBottom: token.marginLG }}>
      <Typography.Text strong>Cópia para um grupo</Typography.Text>
      <Typography.Text type="secondary">
        Gere uma cópia por grupo que a casa semeia. O nome do arquivo é o mesmo para todos; o rótulo só aparece aqui.
      </Typography.Text>
      <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
        <Input
          value={rotulo}
          maxLength={120}
          placeholder="Grupo ENAC SP 1"
          onChange={(evento) => setRotulo(evento.target.value)}
          // Enter repetido durante a geração faria duas cópias com o mesmo rótulo.
          onPressEnter={() => rotulo.trim() && !gerar.isPending && gerar.mutate(rotulo.trim())}
        />
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          disabled={!rotulo.trim()}
          loading={gerar.isPending}
          onClick={() => gerar.mutate(rotulo.trim())}
        >
          Gerar cópia
        </Button>
      </Space.Compact>
    </Space>
  );
}

function UsoDoMaterial({ materialId }: { materialId: string }) {
  const { token } = theme.useToken();
  const engajamento = useQuery({
    queryKey: ['materiais', 'engajamento', materialId, 30],
    queryFn: () => api.materialEngagement(materialId, 30),
    staleTime: 60_000,
  });

  if (engajamento.isPending) return <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  if (engajamento.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={engajamento.error.message}
        action={<Button onClick={() => void engajamento.refetch()}>Tentar novamente</Button>}
      />
    );
  }
  const dados = engajamento.data;

  return (
    <Space orientation="vertical" size={token.marginMD} style={{ width: '100%' }}>
      <Flex gap={token.marginLG} wrap>
        <Numero rotulo="Cliques em links do PDF" valor={dados.links} destaque />
        <Numero rotulo="Contas criadas" valor={dados.contas} destaque />
        <Numero rotulo="Leituras" valor={dados.views} />
        <Numero rotulo="Downloads" valor={dados.downloads} />
        <Numero rotulo="Impressões pedidas" valor={dados.prints} />
        <Numero rotulo="Cópias de grupo" valor={dados.copiasDeGrupo} />
      </Flex>
      <Typography.Text type="secondary">
        Totais acumulados. Contas criadas são as que chegaram por um link de dentro do PDF; downloads não contam as
        cópias de grupo.
      </Typography.Text>
      <Typography.Text strong>Uso nos últimos 30 dias</Typography.Text>
      {dados.serie.length ? (
        <GraficoDeUso serie={dados.serie} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum uso nos últimos 30 dias." />
      )}
      <Typography.Text strong>Cliques por chave</Typography.Text>
      <Table
        size="small"
        rowKey="chave"
        dataSource={dados.porChave}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: 'Nenhum clique ainda.' }}
        columns={[
          { title: 'Chave', dataIndex: 'chave' },
          { title: 'Cliques', dataIndex: 'cliques', align: 'right' },
        ]}
      />
      <Typography.Text strong>Cópias que renderam</Typography.Text>
      <Table
        size="small"
        rowKey="codigo"
        dataSource={dados.copias}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: 640 }}
        locale={{ emptyText: 'Nenhuma cópia de grupo nem cópia com clique ainda.' }}
        columns={[
          {
            title: 'Cópia',
            render: (_, linha) =>
              linha.rotulo ? (
                <Typography.Text strong>{linha.rotulo}</Typography.Text>
              ) : (
                <Typography.Text code>{linha.codigo}</Typography.Text>
              ),
          },
          {
            title: 'Origem',
            render: (_, linha) =>
              linha.rotulo
                ? 'Grupo semeado'
                : [linha.origem?.source, linha.origem?.medium].filter(Boolean).join(' / ') || 'Direto',
          },
          { title: 'Quem pediu', render: (_, linha) => linha.pessoa ?? 'Visitante' },
          { title: 'Quando', render: (_, linha) => formatarData(linha.criadaEm) },
          { title: 'Cliques', dataIndex: 'cliques', align: 'right' },
          { title: 'Contas', dataIndex: 'contas', align: 'right' },
        ]}
      />
    </Space>
  );
}
